/**
 * Production ExplorerInlineFolderNode mocks for browse-focus prototype.
 * - Specs: implementations/archive + many files
 * - Clients mix: many client folders + greater-good with folders AND files
 */

import { useMemo, useState, useCallback } from 'react'
import { useBrowseFolderFocus } from '@/providers/browse-folder-focus-provider'
import {
  ExplorerInlineFolderNode,
  type ExplorerFolderTreeNode,
} from '@/components/layout/explorer-inline-folder-node'

export const SPECS_TREE_ROOT = 'flownotes-app-docs/implementations'
export const SPECS_ARCHIVE = `${SPECS_TREE_ROOT}/archive`
export const SPECS_WS_COLOR = '#14b8a6'
export const CLIENTS_TREE_ROOT = 'hop-studios/clients'
export const GREATER_GOOD_PATH = `${CLIENTS_TREE_ROOT}/greater-good`
export const CLIENTS_WS_COLOR = '#6366f1'
export const PROTOTYPE_WORKSPACE_IDS: string[] = []

/** Subset of Hop clients/ folder names (matches Clients mock tab). */
const CLIENT_FOLDER_NAMES = [
  'greater-good',
  'ggia',
  'ucsf',
  'ISCBC',
  'first-foundation',
  'haven-oakland',
  'hop-business',
  'star',
  'athena-marketing',
  'Azta',
  'Bellyfit',
  'cal-nerds',
  'exida',
  'Cranbrook',
  'designwerke',
  'fuel-integrated',
  'Hillsdale',
  'hop-marketing-sales',
  'bridging-differences-resource-hub',
  'cables-and-lace',
  'CDA',
  'cme-planet',
] as const

function archiveFileName(i: number): string {
  const day = String(3 + (i % 16)).padStart(2, '0')
  return `2025-11-${day}-spec-${i + 1}.md`
}

export function buildSpecsMockTree(): ExplorerFolderTreeNode {
  const files: ExplorerFolderTreeNode[] = Array.from({ length: 28 }, (_, i) => {
    const name = archiveFileName(i)
    return {
      id: `file-${i}`,
      name,
      type: 'file',
      path: `${SPECS_ARCHIVE}/${name}`,
    }
  })
  return {
    id: 'impl',
    name: 'implementations',
    type: 'folder',
    path: SPECS_TREE_ROOT,
    children: [
      {
        id: 'archive',
        name: 'archive',
        type: 'folder',
        path: SPECS_ARCHIVE,
        children: [
          { id: 'y09', name: '2025-09', type: 'folder', path: `${SPECS_ARCHIVE}/2025-09`, children: [] },
          { id: 'y10', name: '2025-10', type: 'folder', path: `${SPECS_ARCHIVE}/2025-10`, children: [] },
          ...files,
          { id: 'y12', name: '2025-12', type: 'folder', path: `${SPECS_ARCHIVE}/2025-12`, children: [] },
        ],
      },
    ],
  }
}

export function buildSpecsActivitySeed(): { filePath: string; timestampMs: number }[] {
  const now = Date.now()
  return Array.from({ length: 28 }, (_, i) => {
    const daysAgo = i < 8 ? 2 : 120
    return {
      filePath: `${SPECS_ARCHIVE}/${archiveFileName(i)}`,
      timestampMs: now - daysAgo * 86_400_000,
    }
  })
}

export function buildClientsMixTree(): ExplorerFolderTreeNode {
  const clientFolders: ExplorerFolderTreeNode[] = CLIENT_FOLDER_NAMES.map((name) => {
    if (name === 'greater-good') {
      const subfolders: ExplorerFolderTreeNode[] = ['ggia', 'greatergood', 'magazines', 'todos'].map(
        (sub) => ({
          id: `gg-${sub}`,
          name: sub,
          type: 'folder' as const,
          path: `${GREATER_GOOD_PATH}/${sub}`,
          children: [],
        })
      )
      const looseFiles: ExplorerFolderTreeNode[] = Array.from({ length: 24 }, (_, i) => {
        const n = `2026-05-${String(10 + (i % 20)).padStart(2, '0')}-note-${i + 1}.md`
        return {
          id: `ggf-${i}`,
          name: n,
          type: 'file' as const,
          path: `${GREATER_GOOD_PATH}/${n}`,
        }
      })
      return {
        id: 'client-greater-good',
        name,
        type: 'folder',
        path: GREATER_GOOD_PATH,
        children: [...subfolders, ...looseFiles],
      }
    }
    return {
      id: `client-${name}`,
      name,
      type: 'folder',
      path: `${CLIENTS_TREE_ROOT}/${name}`,
      children: [],
    }
  })

  return {
    id: 'clients',
    name: 'clients',
    type: 'folder',
    path: CLIENTS_TREE_ROOT,
    children: clientFolders,
  }
}

export function buildClientsMixActivitySeed(): { filePath: string; timestampMs: number }[] {
  const now = Date.now()
  const entries: { filePath: string; timestampMs: number }[] = []

  CLIENT_FOLDER_NAMES.forEach((name, i) => {
    const daysAgo = i < 7 ? 3 : 100
    entries.push({
      filePath: `${CLIENTS_TREE_ROOT}/${name}`,
      timestampMs: now - daysAgo * 86_400_000,
    })
    if (name === 'greater-good') {
      ;['ggia', 'greatergood', 'magazines', 'todos'].forEach((sub, j) => {
        entries.push({
          filePath: `${GREATER_GOOD_PATH}/${sub}`,
          timestampMs: now - (j < 2 ? 5 : 95) * 86_400_000,
        })
      })
      for (let i = 0; i < 24; i++) {
        const n = `2026-05-${String(10 + (i % 20)).padStart(2, '0')}-note-${i + 1}.md`
        entries.push({
          filePath: `${GREATER_GOOD_PATH}/${n}`,
          timestampMs: now - (i < 7 ? 4 : 110) * 86_400_000,
        })
      }
    }
  })

  return entries
}

export function buildPrototypeActivitySeed(): { filePath: string; timestampMs: number }[] {
  return [...buildSpecsActivitySeed(), ...buildClientsMixActivitySeed()]
}

function ProductionTreePanel({
  tree,
  treeRoot,
  wsColor,
  label,
  defaultExpanded,
}: {
  tree: ExplorerFolderTreeNode
  treeRoot: string
  wsColor: string
  label: string
  defaultExpanded: string[]
}) {
  const browseFocus = useBrowseFolderFocus()
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(defaultExpanded))
  const [activePath, setActivePath] = useState<string | null>(null)

  const toggle = useCallback((path: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }, [])

  const focusParent = defaultExpanded[defaultExpanded.length - 1] ?? treeRoot
  const focusChildren =
    focusParent === treeRoot
      ? tree.children ?? []
      : tree.children?.find((c) => c.path === focusParent)?.children ?? []

  const hidden = browseFocus?.hiddenChildrenBreakdown(
    focusChildren,
    focusParent,
    activePath,
    []
  )
  const parentRevealed = browseFocus?.isBrowseParentRevealed(focusParent) ?? false

  return (
    <div className="pl-2 space-y-1">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-1 py-0.5">
        {label}
      </p>
      <ExplorerInlineFolderNode
        node={tree}
        treeRoot={treeRoot}
        wsColor={wsColor}
        activeDocPath={activePath}
        expandedNodes={expanded}
        onToggleExpand={toggle}
        onNodeClick={(path, type) => {
          if (type === 'file') setActivePath(path)
        }}
      />
    </div>
  )
}

export function BrowseSpecsProductionTree() {
  const tree = useMemo(() => buildSpecsMockTree(), [])
  return (
    <ProductionTreePanel
      tree={tree}
      treeRoot={SPECS_TREE_ROOT}
      wsColor={SPECS_WS_COLOR}
      label="Specs: files under archive (28 files)"
      defaultExpanded={[SPECS_TREE_ROOT, SPECS_ARCHIVE]}
    />
  )
}

export function BrowseClientsMixProductionTree() {
  const tree = useMemo(() => buildClientsMixTree(), [])
  return (
    <ProductionTreePanel
      tree={tree}
      treeRoot={CLIENTS_TREE_ROOT}
      wsColor={CLIENTS_WS_COLOR}
      label="Clients: 22 folders; greater-good has 4 subfolders + 24 files (file focus)"
      defaultExpanded={[CLIENTS_TREE_ROOT, GREATER_GOOD_PATH]}
    />
  )
}
